/**
 * API Service - Frontend client for Printy API
 *
 * Calls the Cloudflare Pages Functions for:
 * - Model scraping (secure Gemini API calls)
 * - Price calculation
 */

import type { Order, OrderStatus, ScrapedModelData } from '../types';

// API base URL - empty for same-origin (Cloudflare Pages Functions)
const API_BASE = '';

/**
 * Scrape model data from MakerWorld or Printables URL
 */
export async function scrapeModel(url: string): Promise<ScrapedModelData> {
  try {
    const response = await fetch(`${API_BASE}/api/scrape-model`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return {
      name: data.name,
      printTime: data.printTime,
      filamentGrams: data.filamentGrams,
      imageUrl: data.imageUrl,
    };
  } catch (error) {
    // Only use fallback in development, fail properly in production
    if (import.meta.env.DEV) {
      console.warn('API call failed, using development fallback:', error);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            name: 'Demo Model (API Unavailable)',
            printTime: 3.5,
            filamentGrams: 85,
            imageUrl: 'https://placehold.co/400x300/8b5cf6/ffffff?text=Demo+Mode',
          });
        }, 800);
      });
    }
    throw error;
  }
}

/**
 * Calculate price based on print time and filament usage
 */
export async function calculatePrice(
  printTimeHours: number,
  filamentGrams: number
): Promise<number> {
  try {
    const response = await fetch(`${API_BASE}/api/calculate-price`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ printTimeHours, filamentGrams }),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }

    const data = await response.json();
    return data.price;
  } catch (error) {
    // Only use fallback in development, fail properly in production
    if (import.meta.env.DEV) {
      console.warn('API call failed, using development fallback calculation:', error);
      const PRICING = {
        KWH_RATE: 0.48,
        PRINTER_WATTS: 150,
        COST_PER_GRAM: 0.05,
        MARKUP_MULTIPLIER: 1.5,
      };

      const electricityCost = ((printTimeHours * PRICING.PRINTER_WATTS) / 1000) * PRICING.KWH_RATE;
      const filamentCost = filamentGrams * PRICING.COST_PER_GRAM;
      const totalCost = (electricityCost + filamentCost) * PRICING.MARKUP_MULTIPLIER;

      return Math.ceil(totalCost * 2) / 2;
    }
    throw error;
  }
}

/**
 * Fetch orders from database
 */
export async function fetchOrders(): Promise<Order[]> {
  try {
    const response = await fetch(`${API_BASE}/api/orders`);
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
    const data = await response.json();
    return data.orders || [];
  } catch (error) {
    console.error('Failed to fetch orders:', error);
    return [];
  }
}

/**
 * Update order status
 */
export async function updateOrderStatus(orderId: number, status: OrderStatus): Promise<void> {
  try {
    const response = await fetch(`${API_BASE}/api/orders/${orderId}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!response.ok) {
      throw new Error(`API error: ${response.status}`);
    }
  } catch (error) {
    console.error('Failed to update order status:', error);
    throw error;
  }
}

/**
 * Cancel order (buyer) - cancels directly if pending, requests cancellation otherwise
 */
export async function cancelOrder(
  orderId: number,
  reason?: string
): Promise<{ cancelled: boolean; cancellationRequested: boolean; message: string }> {
  const token = localStorage.getItem('sessionToken');
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${API_BASE}/api/orders/${orderId}/cancel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ reason }),
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error || 'Failed to cancel order');
  }

  return {
    cancelled: data.cancelled || false,
    cancellationRequested: data.cancellationRequested || false,
    message: data.message,
  };
}

/**
 * Admin set price on an order
 */
export async function adminSetPrice(orderId: number, price: number): Promise<void> {
  const token = localStorage.getItem('printy_admin_token');
  if (!token) {
    throw new Error('Admin not authenticated');
  }

  const response = await fetch(`${API_BASE}/api/admin/orders/${orderId}/price`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ price }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to set price');
  }
}

/**
 * Admin cancel order - can cancel any order or handle cancellation requests
 */
export async function adminCancelOrder(
  orderId: number,
  action: 'cancel' | 'approve' | 'deny' = 'cancel'
): Promise<void> {
  const token = localStorage.getItem('printy_admin_token');
  if (!token) {
    throw new Error('Admin not authenticated');
  }

  const response = await fetch(`${API_BASE}/api/admin/orders/${orderId}/cancel`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ action }),
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to process cancellation');
  }
}

/**
 * Admin permanently delete a cancelled order
 */
export async function adminDeleteOrder(orderId: number): Promise<void> {
  const token = localStorage.getItem('printy_admin_token');
  if (!token) {
    throw new Error('Admin not authenticated');
  }

  const response = await fetch(`${API_BASE}/api/admin/orders/${orderId}/delete`, {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const data = await response.json();
    throw new Error(data.error || 'Failed to delete order');
  }
}
